import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import ProposalCard from './ProposalCard';
import '../../css/MainProjectComponent.css';

const endpointpath = process.env.REACT_APP_API_BASE_URL;

const safeListText = (v, fallback = '') => {
  if (v == null || v === '') return fallback;
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return String(v);
  return fallback || '—';
};

const normalizeAuthToken = (rawToken) => {
  if (!rawToken) return '';
  return rawToken.startsWith('Bearer ') ? rawToken : `Bearer ${rawToken}`;
};

const buildAuthHeaders = () => {
  const rawToken = localStorage.getItem('token');
  if (!rawToken) return null;
  return { Authorization: normalizeAuthToken(rawToken) };
};

const ProposalViewer = () => {
  const navigate = useNavigate();
  const { projectID } = useParams();

  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedProposalId, setSelectedProposalId] = useState(null);

  const fetchProposals = useCallback(async (_projectIdFromCard) => {
    try {
      setLoading(true);
      setError('');
      const headers = buildAuthHeaders();
      if (!headers) {
        setError('Session expired. Please log in again.');
        setLoading(false);
        return;
      }

      const response = await fetch(
        `${endpointpath}/api/displayproposals?ProjectID=${encodeURIComponent(projectID)}`,
        {
          method: 'GET',
          headers,
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch proposals (${response.status})`);
      }

      const data = await response.json();
      const next = Array.isArray(data?.proposals) ? data.proposals : [];
      setProposals(next);
      setSelectedProposalId((prev) => prev ?? (next[0]?.proposalID ?? null));
      setLoading(false);
    } catch (e) {
      setError('Error fetching proposals.');
      setLoading(false);
    }
  }, [projectID]);

  useEffect(() => {
    fetchProposals();
  }, [fetchProposals]);

  const selectedProposal = useMemo(() => {
    if (!selectedProposalId) return proposals[0] ?? null;
    return proposals.find((p) => p.proposalID === selectedProposalId) ?? null;
  }, [proposals, selectedProposalId]);

  /** Return to the projects list with this project pre-selected (MainProject reads `projectID` from the query string). */
  const handleBack = () => {
    navigate(`/projects?projectID=${encodeURIComponent(projectID)}`);
  };

  return (
    <div className="container">
      <div className="left-pane">
        <Box className="content-container">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Typography variant="h5" fontWeight="bold">
              Proposal Viewer
            </Typography>
            <Button
              variant="contained"
              onClick={handleBack}
              sx={{
                backgroundColor: '#e9e4dc',
                color: '#500000',
                fontWeight: 700,
                border: '2px solid #500000',
                '&:hover': { backgroundColor: '#ded7cc' },
              }}
            >
              Back to Projects
            </Button>
          </Box>

          <Divider sx={{ my: 1.5 }} />

          {loading ? (
            <Typography>Loading proposals...</Typography>
          ) : error ? (
            <Typography color="error">{error}</Typography>
          ) : !selectedProposal ? (
            <Typography sx={{ color: '#707070', fontWeight: 700 }}>
              Yet to receive proposals
            </Typography>
          ) : (
            <Box sx={{ width: '100%', minHeight: 0, overflow: 'auto' }}>
              <ProposalCard
                proposal={selectedProposal}
                proposals={proposals}
                fetchProposals={fetchProposals}
                isExpanded={true}
                onToggleExpand={() => {}}
              />
            </Box>
          )}
        </Box>
      </div>

      <div className="right-pane">
        <Box
          display="flex"
          flexDirection="column"
          height="100%"
          width="100%"
          sx={{ minHeight: 0 }}
        >
          <Box padding={2} borderBottom="1px solid #ccc" bgcolor="#e9e4dc" position="sticky" top={0} zIndex={2}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
              List of Proposals
            </Typography>
            <Typography variant="body2" sx={{ color: '#535353' }}>
              Showing proposals submitted to this project only.
            </Typography>
          </Box>

          <Box flexGrow={1} overflow="auto" padding={1} minHeight={0}>
            {loading ? (
              <Typography sx={{ p: 2 }}>Loading…</Typography>
            ) : proposals.length === 0 ? (
              <Typography sx={{ p: 2, color: '#707070', fontWeight: 700 }}>
                Yet to receive proposals
              </Typography>
            ) : (
              <List disablePadding>
                {proposals.map((p) => {
                  const selected = p.proposalID === selectedProposalId;
                  return (
                    <Box key={p.proposalID} sx={{ mb: 0.75, px: 0.5 }}>
                      <ListItemButton
                        selected={selected}
                        onClick={() => setSelectedProposalId(p.proposalID)}
                        sx={{
                          border: '1px solid #c7c7c7',
                          borderRadius: 1,
                          bgcolor: selected ? '#ffffff' : '#f6f6f6',
                          '&.Mui-selected': { bgcolor: '#ffffff' },
                          '&.Mui-selected:hover': { bgcolor: '#ffffff' },
                        }}
                      >
                        <ListItemText
                          primary={
                            <Typography sx={{ fontWeight: 800, color: '#1f1f1f' }}>
                              {safeListText(p.proposalTitle, 'Untitled Proposal')}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="body2" sx={{ color: '#535353' }}>
                              {safeListText(p.applicantName)
                                ? `Submitted by: ${safeListText(p.applicantName)}`
                                : 'Submitted by: N/A'}
                            </Typography>
                          }
                        />
                      </ListItemButton>
                    </Box>
                  );
                })}
              </List>
            )}
          </Box>
        </Box>
      </div>
    </div>
  );
};

export default ProposalViewer;

